import { Download, FileText, Plus, Save, Upload } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/occurrences/button'
import {
	ConfidentialityBadge,
	Field,
	Notice,
	Panel,
	PhotoPlaceholder,
	ReadOnlyValue,
} from '@/components/occurrences/occurrence-ui'
import { type PdfKind, PdfPreview } from '@/components/occurrences/pdf-preview'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import {
	CAUSAL_LINKS,
	DOC_CATEGORIES,
	MEDICAL_SUBJECTS,
} from '@/mocks/occurrences/catalog'
import {
	can,
	canViewConfidentiality,
	getTransitions,
	progressStages,
} from '@/mocks/occurrences/rules'
import { newId, useOccurrenceMock } from '@/mocks/occurrences/store'
import type {
	Confidentiality,
	LessonLearned,
	MedicalData,
	OccurrenceDocument,
} from '@/types/occurrence'
import { CONFIDENTIALITY_LABEL } from '@/types/occurrence'
import { useOccurrence } from './occurrence-context'

const br = (d: string) => (d ? d.split('-').reverse().join('/') : '—')
const now = () => new Date().toISOString().slice(0, 16)

// ─── Lição aprendida ────────────────────────────────────────────────────────
export const LessonSection = () => {
	const { occ, profile, editable, userName } = useOccurrence()
	const update = useOccurrenceMock((s) => s.update)
	const empty: LessonLearned = {
		service: '',
		activity: '',
		summary: '',
		learnings: '',
		recommendations: '',
		keywords: [],
		photoIds: [],
		validated: false,
	}
	const [l, setL] = useState<LessonLearned>(occ.lesson ?? empty)
	const [kw, setKw] = useState((occ.lesson?.keywords ?? []).join(', '))
	const [pdf, setPdf] = useState(false)
	const [saved, setSaved] = useState(false)
	const photos = occ.evidences.filter(
		(e) => !e.deletedReason && e.confidentiality !== 'RESTRICTED',
	)
	const ro = !editable || l.validated
	const set = <K extends keyof LessonLearned>(k: K, v: LessonLearned[K]) => {
		setSaved(false)
		setL((s) => ({ ...s, [k]: v }))
	}
	const toggle = (id: string) =>
		set(
			'photoIds',
			l.photoIds.includes(id)
				? l.photoIds.filter((x) => x !== id)
				: l.photoIds.length < 3
					? [...l.photoIds, id]
					: l.photoIds,
		)
	const persist = (next: LessonLearned, event: string) =>
		update(occ.id, { lesson: next }, event)
	const revision =
		occ.documents.filter((d) => d.category === 'Lição aprendida').length + 1

	return (
		<div className="space-y-4">
			<Panel
				title="Lição aprendida"
				description="Até três fotos principais, resumo, aprendizados, recomendações e palavras-chave para pesquisa."
			>
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<Field label="Serviço" required>
						<Input
							disabled={ro}
							value={l.service}
							onChange={(e) => set('service', e.target.value)}
						/>
					</Field>
					<Field label="Atividade relacionada">
						<Input
							disabled={ro}
							value={l.activity}
							onChange={(e) => set('activity', e.target.value)}
						/>
					</Field>
				</div>
				<Field label="Resumo do evento" required>
					<Textarea
						rows={3}
						disabled={ro}
						value={l.summary}
						onChange={(e) => set('summary', e.target.value)}
					/>
				</Field>
				<Field label="Aprendizados">
					<Textarea
						rows={3}
						disabled={ro}
						value={l.learnings}
						onChange={(e) => set('learnings', e.target.value)}
					/>
				</Field>
				<Field label="Recomendações">
					<Textarea
						rows={3}
						disabled={ro}
						value={l.recommendations}
						onChange={(e) => set('recommendations', e.target.value)}
					/>
				</Field>
				<Field label="Palavras-chave" hint="Separadas por vírgula">
					<Input
						disabled={ro}
						value={kw}
						onChange={(e) => {
							setKw(e.target.value)
							set(
								'keywords',
								e.target.value
									.split(',')
									.map((x) => x.trim())
									.filter(Boolean),
							)
						}}
					/>
				</Field>
				<Field
					label={`Fotos principais (${l.photoIds.length}/3)`}
					hint="Evidências restritas não podem ser usadas"
				>
					<div className="flex flex-wrap gap-2">
						{photos.map((p) => (
							<button
								type="button"
								key={p.id}
								disabled={ro}
								onClick={() => toggle(p.id)}
								className={cn(
									'overflow-hidden rounded-lg border-2',
									l.photoIds.includes(p.id)
										? 'border-kaefer-dark-grey'
										: 'border-transparent opacity-70',
								)}
							>
								<PhotoPlaceholder
									tone={p.tone}
									className="h-16 w-24"
									label={p.caption}
								/>
							</button>
						))}
					</div>
				</Field>
				<div className="flex flex-wrap items-center justify-end gap-2">
					{saved && (
						<span className="text-sm text-kaefer-forest-green">Salvo</span>
					)}
					{!ro && (
						<Button
							variant="outline"
							className="gap-1.5"
							onClick={() => {
								persist(l, 'Lição aprendida salva')
								setSaved(true)
							}}
						>
							<Save className="size-4" /> Salvar rascunho
						</Button>
					)}
					{!ro && can(profile, 'concludeInvestigation') && (
						<Button
							variant="primary"
							disabled={!l.service || !l.summary}
							onClick={() =>
								persist({ ...l, validated: true }, 'Lição aprendida validada')
							}
						>
							Validar
						</Button>
					)}
					{l.validated && (
						<Button
							variant="primary"
							className="gap-1.5"
							onClick={() => setPdf(true)}
						>
							<FileText className="size-4" /> Gerar PDF
						</Button>
					)}
				</div>
				{l.validated && (
					<Notice>
						Validada por {userName}. O PDF vertical é gerado com filial,
						ocorrência, revisão e data de emissão.
					</Notice>
				)}
			</Panel>
			<PdfPreview
				open={pdf}
				onOpenChange={setPdf}
				kind="LESSON"
				occ={{ ...occ, lesson: l }}
				revision={revision}
				onConfirm={() =>
					update(
						occ.id,
						{
							documents: [
								...occ.documents,
								{
									id: newId(),
									name: `LicaoAprendida-${occ.number}-rev${revision}.pdf`,
									category: 'Lição aprendida',
									version: revision,
									confidentiality: 'INTERNAL',
									retention: '5 anos',
									author: userName,
									date: now().slice(0, 10),
									downloads: [],
								},
							],
						},
						'PDF de lição aprendida gerado',
						{ detail: `Revisão ${revision}` },
					)
				}
			/>
		</div>
	)
}

// ─── Dados médicos / CAT ────────────────────────────────────────────────────
const EMPTY_MED: MedicalData = {
	careDate: '',
	careTime: '',
	doctor: '',
	crm: '',
	cid: '',
	bodySide: 'Esquerdo',
	externalTreatment: 'Não',
	bodyPart: '',
	causalLink: CAUSAL_LINKS[0],
	catIssued: false,
	leaveDays: 0,
	records: [],
}

export const MedicalSection = () => {
	const { occ, profile, userName } = useOccurrence()
	const update = useOccurrenceMock((s) => s.update)
	const audit = useOccurrenceMock((s) => s.audit)
	const clinical = can(profile, 'viewMedical')
	const edit = can(profile, 'editMedical')
	const [m, setM] = useState<MedicalData>(occ.medical ?? EMPTY_MED)
	const [adding, setAdding] = useState(false)
	const [rec, setRec] = useState({ subject: MEDICAL_SUBJECTS[0], note: '' })
	const [saved, setSaved] = useState(false)
	const logged = useRef(false)
	const set = <K extends keyof MedicalData>(k: K, v: MedicalData[K]) => {
		setSaved(false)
		setM((s) => ({ ...s, [k]: v }))
	}

	// Toda consulta clínica é auditada
	useEffect(() => {
		if (clinical && !logged.current) {
			logged.current = true
			audit(occ.id, 'Consulta ao prontuário / dados clínicos', {
				sensitive: true,
			})
		}
	}, [clinical, audit, occ.id])

	return (
		<div className="space-y-4">
			<Panel
				title="CAT — dados administrativos"
				description="A CAT é emitida no Protheus. O aplicativo guarda o PDF e o vínculo administrativo, sem substituir o sistema de origem."
			>
				<div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
					<ReadOnlyValue
						label="CAT emitida"
						value={m.catIssued ? 'Sim' : 'Não'}
					/>
					<ReadOnlyValue
						label="Data de emissão"
						value={br(m.catIssuedAt ?? '')}
					/>
					<ReadOnlyValue
						label="Dias de afastamento"
						value={String(m.leaveDays)}
					/>
					<ReadOnlyValue label="PDF da CAT" value={m.catPdf ?? 'Não anexado'} />
				</div>
				{edit && (
					<div className="grid grid-cols-1 items-end gap-3 border-t pt-3 sm:grid-cols-4">
						<Field label="CAT emitida no Protheus">
							<NativeSelect
								value={m.catIssued ? 'Sim' : 'Não'}
								onChange={(e) => set('catIssued', e.target.value === 'Sim')}
							>
								<NativeSelectOption>Sim</NativeSelectOption>
								<NativeSelectOption>Não</NativeSelectOption>
							</NativeSelect>
						</Field>
						<Field label="Data de emissão">
							<Input
								type="date"
								value={m.catIssuedAt ?? ''}
								onChange={(e) => set('catIssuedAt', e.target.value)}
							/>
						</Field>
						<Field label="Dias de afastamento">
							<Input
								type="number"
								value={m.leaveDays}
								onChange={(e) => set('leaveDays', Number(e.target.value))}
							/>
						</Field>
						<Button
							variant="outline"
							className="gap-1.5"
							onClick={() => set('catPdf', `CAT-${occ.number}.pdf`)}
						>
							<Upload className="size-4" /> Anexar PDF
						</Button>
					</div>
				)}
			</Panel>

			{!clinical ? (
				<Notice tone="lock">
					Dados clínicos (diagnóstico, evolução, exames e observações médicas)
					são restritos à Medicina e não são exibidos para o seu perfil.
				</Notice>
			) : (
				<>
					<Panel
						title="Dados médicos"
						description="Todos os dados são necessários para abrir a CAT. Acesso, alteração e exportação são auditados."
					>
						<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
							<Field label="Data do atendimento">
								<Input
									type="date"
									disabled={!edit}
									value={m.careDate}
									onChange={(e) => set('careDate', e.target.value)}
								/>
							</Field>
							<Field label="Hora">
								<Input
									type="time"
									disabled={!edit}
									value={m.careTime}
									onChange={(e) => set('careTime', e.target.value)}
								/>
							</Field>
							<Field label="Nome do médico">
								<Input
									disabled={!edit}
									value={m.doctor}
									onChange={(e) => set('doctor', e.target.value)}
								/>
							</Field>
							<Field label="CRM">
								<Input
									disabled={!edit}
									value={m.crm}
									onChange={(e) => set('crm', e.target.value)}
								/>
							</Field>
							<Field label="CID">
								<Input
									disabled={!edit}
									value={m.cid}
									onChange={(e) => set('cid', e.target.value)}
								/>
							</Field>
							<Field label="Lado do corpo">
								<NativeSelect
									disabled={!edit}
									value={m.bodySide}
									onChange={(e) => set('bodySide', e.target.value)}
								>
									{['Esquerdo', 'Direito', 'Ambos', 'N/A'].map((x) => (
										<NativeSelectOption key={x}>{x}</NativeSelectOption>
									))}
								</NativeSelect>
							</Field>
							<Field label="Encaminhado a tratamento externo">
								<NativeSelect
									disabled={!edit}
									value={m.externalTreatment}
									onChange={(e) => set('externalTreatment', e.target.value)}
								>
									<NativeSelectOption>Sim</NativeSelectOption>
									<NativeSelectOption>Não</NativeSelectOption>
								</NativeSelect>
							</Field>
							<Field label="Parte do corpo simplificada">
								<Input
									disabled={!edit}
									value={m.bodyPart}
									onChange={(e) => set('bodyPart', e.target.value)}
								/>
							</Field>
							<Field label="Nexo causal">
								<NativeSelect
									disabled={!edit}
									value={m.causalLink}
									onChange={(e) => set('causalLink', e.target.value)}
								>
									{CAUSAL_LINKS.map((x) => (
										<NativeSelectOption key={x}>{x}</NativeSelectOption>
									))}
								</NativeSelect>
							</Field>
						</div>
						{edit && (
							<div className="flex items-center justify-end gap-3">
								{saved && (
									<span className="text-sm text-kaefer-forest-green">
										Salvo (versionado)
									</span>
								)}
								<Button
									variant="primary"
									className="gap-2 font-semibold"
									onClick={() => {
										update(occ.id, { medical: m }, 'Dados médicos alterados', {
											sensitive: true,
										})
										setSaved(true)
									}}
								>
									<Save className="size-4" /> Salvar
								</Button>
							</div>
						)}
					</Panel>

					<Panel
						title="Prontuário médico"
						description="Uso exclusivo da Medicina do Trabalho, com versionamento e auditoria."
						actions={
							edit && (
								<Button
									size="sm"
									variant="primary"
									className="gap-1.5"
									onClick={() => setAdding(true)}
								>
									<Plus className="size-4" /> Incluir registro
								</Button>
							)
						}
					>
						{m.records.length === 0 && (
							<p className="text-sm text-muted-foreground">Sem registros.</p>
						)}
						<ul className="divide-y">
							{m.records.map((r) => (
								<li key={r.id} className="py-2.5 text-sm">
									<p className="font-medium text-kaefer-dark-grey">
										{r.subject}{' '}
										<span className="font-normal text-muted-foreground">
											· {br(r.date)} · {r.by}
										</span>
									</p>
									<p className="text-muted-foreground">{r.note}</p>
								</li>
							))}
						</ul>
					</Panel>
					<Dialog open={adding} onOpenChange={setAdding}>
						<DialogContent className="sm:max-w-md">
							<DialogHeader>
								<DialogTitle>Inclusão de registro no prontuário</DialogTitle>
							</DialogHeader>
							<div className="space-y-3">
								<Field label="Assunto" required>
									<NativeSelect
										value={rec.subject}
										onChange={(e) =>
											setRec({ ...rec, subject: e.target.value })
										}
									>
										{MEDICAL_SUBJECTS.map((s) => (
											<NativeSelectOption key={s}>{s}</NativeSelectOption>
										))}
									</NativeSelect>
								</Field>
								<Field label="Observação">
									<Textarea
										rows={5}
										value={rec.note}
										onChange={(e) => setRec({ ...rec, note: e.target.value })}
									/>
								</Field>
								<p className="text-xs text-muted-foreground">
									Responsável pelo registro: {userName}
								</p>
							</div>
							<div className="flex justify-end gap-2">
								<Button variant="cancel" onClick={() => setAdding(false)}>
									Cancelar
								</Button>
								<Button
									variant="primary"
									onClick={() => {
										const next = {
											...m,
											records: [
												...m.records,
												{
													id: newId(),
													date: now().slice(0, 10),
													subject: rec.subject,
													note: rec.note,
													by: userName,
												},
											],
										}
										setM(next)
										update(
											occ.id,
											{ medical: next },
											'Registro incluído no prontuário',
											{ sensitive: true, detail: rec.subject },
										)
										setRec({ subject: MEDICAL_SUBJECTS[0], note: '' })
										setAdding(false)
									}}
								>
									Salvar
								</Button>
							</div>
						</DialogContent>
					</Dialog>
				</>
			)}
		</div>
	)
}

// ─── Documentos ─────────────────────────────────────────────────────────────
export const DocumentsSection = () => {
	const { occ, profile, editable, userName } = useOccurrence()
	const update = useOccurrenceMock((s) => s.update)
	const [cat, setCat] = useState(DOC_CATEGORIES[0])
	const [conf, setConf] = useState<Confidentiality>('INTERNAL')
	const [name, setName] = useState('')
	const [pdf, setPdf] = useState<PdfKind | null>(null)
	const docs = occ.documents.filter((d) =>
		canViewConfidentiality(profile, d.confidentiality),
	)
	const hidden = occ.documents.length - docs.length
	const alertRev =
		occ.documents.filter((d) => d.category === 'Alerta').length + 1

	const download = (d: OccurrenceDocument) =>
		update(
			occ.id,
			{
				documents: occ.documents.map((x) =>
					x.id === d.id
						? { ...x, downloads: [...x.downloads, { by: userName, at: now() }] }
						: x,
				),
			},
			`Download: ${d.name}`,
			{ sensitive: d.confidentiality === 'RESTRICTED' },
		)

	const upload = () => {
		const existing = occ.documents.filter(
			(d) =>
				d.name === name ||
				(d.category === cat && d.name.startsWith(name.split('.')[0])),
		).length
		update(
			occ.id,
			{
				documents: [
					...occ.documents,
					{
						id: newId(),
						name: name || `documento-${occ.documents.length + 1}.pdf`,
						category: cat,
						version: existing + 1,
						confidentiality: conf,
						retention: conf === 'RESTRICTED' ? '20 anos' : '5 anos',
						author: userName,
						date: now().slice(0, 10),
						downloads: [],
					},
				],
			},
			'Documento enviado',
			{
				detail: `${cat} · ${CONFIDENTIALITY_LABEL[conf]} · antivírus: OK (mock)`,
			},
		)
		setName('')
	}

	return (
		<div className="space-y-4">
			<Panel
				title="Documentos da ocorrência"
				description="Registro, classificação, versionamento e controle de download. Downloads de conteúdo restrito são auditados."
			>
				{hidden > 0 && (
					<Notice tone="lock">
						{hidden} documento(s) restrito(s) ocultos para o seu perfil.
					</Notice>
				)}
				{docs.length === 0 ? (
					<p className="text-sm text-muted-foreground">Nenhum documento.</p>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full min-w-[680px] text-sm">
							<thead>
								<tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
									<th className="py-2">Documento</th>
									<th>Categoria</th>
									<th>Versão</th>
									<th>Confidencialidade</th>
									<th>Retenção</th>
									<th>Downloads</th>
									<th />
								</tr>
							</thead>
							<tbody>
								{docs.map((d) => (
									<tr key={d.id} className="border-b last:border-0">
										<td className="py-2.5">
											<p>{d.name}</p>
											<p className="text-[11px] text-muted-foreground">
												{d.author} · {br(d.date)}
											</p>
										</td>
										<td className="text-xs">{d.category}</td>
										<td className="text-xs">v{d.version}</td>
										<td>
											<ConfidentialityBadge value={d.confidentiality} />
										</td>
										<td className="text-xs">{d.retention}</td>
										<td className="text-xs">{d.downloads.length}</td>
										<td className="text-right">
											<Button
												size="sm"
												variant="ghost"
												className="gap-1"
												onClick={() => download(d)}
											>
												<Download className="size-4" />
											</Button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
				{editable && (
					<div className="grid grid-cols-1 items-end gap-3 border-t pt-4 sm:grid-cols-[1fr_160px_140px_auto]">
						<Field label="Nome do arquivo">
							<Input
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="arquivo.pdf"
							/>
						</Field>
						<Field label="Categoria">
							<NativeSelect
								value={cat}
								onChange={(e) => setCat(e.target.value)}
							>
								{DOC_CATEGORIES.map((c) => (
									<NativeSelectOption key={c}>{c}</NativeSelectOption>
								))}
							</NativeSelect>
						</Field>
						<Field label="Confidencialidade">
							<NativeSelect
								value={conf}
								onChange={(e) => setConf(e.target.value as Confidentiality)}
							>
								{(Object.keys(CONFIDENTIALITY_LABEL) as Confidentiality[]).map(
									(c) => (
										<NativeSelectOption key={c} value={c}>
											{CONFIDENTIALITY_LABEL[c]}
										</NativeSelectOption>
									),
								)}
							</NativeSelect>
						</Field>
						<Button variant="primary" className="gap-1.5" onClick={upload}>
							<Upload className="size-4" /> Enviar
						</Button>
					</div>
				)}
			</Panel>

			<Panel
				title="Relatórios em PDF"
				description="Modelos corporativos versionados; conteúdo controlado por perfil e dados restritos omitidos."
			>
				<div className="flex flex-wrap items-center gap-3">
					<Button
						variant="outline"
						className="gap-1.5"
						onClick={() => setPdf('ALERT')}
					>
						<FileText className="size-4" /> Gerar alerta de comunicação
					</Button>
					<span className="text-xs text-muted-foreground">
						Próxima revisão: {alertRev}
					</span>
				</div>
			</Panel>
			<PdfPreview
				open={pdf === 'ALERT'}
				onOpenChange={(v) => !v && setPdf(null)}
				kind="ALERT"
				occ={occ}
				revision={alertRev}
				onConfirm={() =>
					update(
						occ.id,
						{
							documents: [
								...occ.documents,
								{
									id: newId(),
									name: `Alerta-${occ.number}-rev${alertRev}.pdf`,
									category: 'Alerta',
									version: alertRev,
									confidentiality: 'INTERNAL',
									retention: '5 anos',
									author: userName,
									date: now().slice(0, 10),
									downloads: [],
								},
							],
						},
						'PDF de alerta gerado',
						{ detail: `Revisão ${alertRev}` },
					)
				}
			/>
		</div>
	)
}

// ─── Encerramento ───────────────────────────────────────────────────────────
export const ClosureSection = () => {
	const { occ, profile } = useOccurrence()
	const update = useOccurrenceMock((s) => s.update)
	const transition = useOccurrenceMock((s) => s.transition)
	const [note, setNote] = useState(occ.closure.note)
	const [saved, setSaved] = useState(false)
	const closeT = getTransitions(occ, profile).find((t) => t.to === 'CLOSED')
	const stages = progressStages(occ).filter(
		(s) => s.applicable && s.key !== 'clo',
	)
	const done = occ.actions.filter((a) => a.status === 'DONE')
	const canNote = can(profile, 'close') && occ.status === 'AWAITING_EFFICACY'

	return (
		<div className="space-y-4">
			<Panel
				title="Encerramento"
				description="Bloqueado enquanto houver pendências obrigatórias ou ações sem avaliação aplicável."
			>
				<ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
					{stages.map((s) => (
						<li
							key={s.key}
							className={cn(
								'rounded px-2 py-1 text-sm',
								s.done ? 'bg-kaefer-grass-green/10' : 'bg-kaefer-red/5',
							)}
						>
							{s.done ? '✓' : '✗'} {s.label}
						</li>
					))}
				</ul>
			</Panel>
			<Panel title="Eficácia consolidada das ações">
				{occ.actions.length === 0 ? (
					<p className="text-sm text-muted-foreground">Sem ações.</p>
				) : (
					<ul className="divide-y text-sm">
						{occ.actions.map((a) => (
							<li
								key={a.id}
								className="flex items-center justify-between gap-2 py-2"
							>
								<span className="line-clamp-1">{a.description}</span>
								<span className="shrink-0 text-xs">
									{a.status === 'CANCELLED'
										? 'Cancelada'
										: a.efficacy === 'EFFECTIVE'
											? 'Eficaz'
											: a.efficacy === 'INEFFECTIVE'
												? 'Ineficaz'
												: a.status === 'DONE'
													? 'Sem avaliação'
													: 'Em aberto'}
								</span>
							</li>
						))}
					</ul>
				)}
				{done.some((a) => a.efficacy === 'INEFFECTIVE') && (
					<Notice tone="warn">
						Há ação(ões) ineficaz(es): considere retornar ao plano de ação antes
						de encerrar.
					</Notice>
				)}
			</Panel>
			<Panel title="Decisão de encerramento">
				<Textarea
					rows={4}
					disabled={!canNote}
					value={note}
					onChange={(e) => {
						setNote(e.target.value)
						setSaved(false)
					}}
					placeholder="Consolide a decisão e a fundamentação do encerramento"
				/>
				<div className="flex flex-wrap items-center justify-end gap-3">
					{saved && (
						<span className="text-sm text-kaefer-forest-green">Salvo</span>
					)}
					{canNote && (
						<Button
							variant="outline"
							onClick={() => {
								update(
									occ.id,
									{ closure: { ...occ.closure, note } },
									'Decisão de encerramento registrada',
								)
								setSaved(true)
							}}
						>
							Salvar decisão
						</Button>
					)}
					{closeT && (
						<Button
							variant="primary"
							className="font-semibold"
							disabled={!closeT.allowed}
							title={closeT.blockers.map((b) => b.text).join(' · ')}
							onClick={() => transition(occ.id, 'CLOSED')}
						>
							Encerrar ocorrência
						</Button>
					)}
				</div>
				{closeT && !closeT.allowed && (
					<p className="text-xs text-muted-foreground">
						Bloqueios: {closeT.blockers.map((b) => b.text).join(' · ')}
					</p>
				)}
				{occ.status === 'CLOSED' && (
					<Notice>
						Encerrada por {occ.closure.decidedBy} em{' '}
						{occ.closure.decidedAt?.replace('T', ' ')}.
					</Notice>
				)}
				{occ.status !== 'AWAITING_EFFICACY' && occ.status !== 'CLOSED' && (
					<Notice>
						O encerramento fica disponível quando a ocorrência chegar a
						“Aguardando avaliação de eficácia”.
					</Notice>
				)}
			</Panel>
		</div>
	)
}

// ─── Auditoria ──────────────────────────────────────────────────────────────
export const AuditSection = () => {
	const { occ, profile, userName } = useOccurrence()
	const audit = useOccurrenceMock((s) => s.audit)
	const [onlySensitive, setOnlySensitive] = useState(false)
	const rows = occ.audit.filter((a) => !onlySensitive || a.sensitive)

	return (
		<div className="space-y-4">
			<Panel
				title="Trilha de auditoria"
				description="Imutável para usuários do negócio: criação, consulta sensível, alteração (valor anterior/novo), upload, exclusão lógica, aprovações, exportação e download."
				actions={
					<div className="flex items-center gap-2">
						<label className="flex items-center gap-1.5 text-xs">
							<input
								type="checkbox"
								checked={onlySensitive}
								onChange={(e) => setOnlySensitive(e.target.checked)}
							/>{' '}
							Somente sensíveis
						</label>
						{can(profile, 'export') && (
							<Button
								size="sm"
								variant="outline"
								className="gap-1"
								onClick={() =>
									audit(occ.id, 'Exportação da trilha de auditoria', {
										detail: `por ${userName}`,
									})
								}
							>
								<Download className="size-4" /> Exportar
							</Button>
						)}
					</div>
				}
			>
				<div className="overflow-x-auto">
					<table className="w-full min-w-[640px] text-sm">
						<thead>
							<tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
								<th className="py-2">Data/hora</th>
								<th>Usuário</th>
								<th>Evento</th>
								<th>Detalhe</th>
							</tr>
						</thead>
						<tbody>
							{rows.map((a) => (
								<tr
									key={a.id}
									className={cn(
										'border-b align-top last:border-0',
										a.sensitive && 'bg-kaefer-red/5',
									)}
								>
									<td className="py-2 text-xs whitespace-nowrap">
										{a.at.replace('T', ' ')}
									</td>
									<td className="text-xs">{a.by}</td>
									<td className="text-xs font-medium">
										{a.event}
										{a.sensitive && (
											<span className="ml-1 rounded bg-kaefer-red/15 px-1 text-[10px] text-kaefer-red">
												sensível
											</span>
										)}
									</td>
									<td className="text-xs text-muted-foreground">
										{a.diff ? (
											<>
												{a.diff.field}: <s>{a.diff.from}</s> →{' '}
												<b>{a.diff.to}</b>
											</>
										) : (
											a.detail
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</Panel>
			<Panel title="Histórico de status">
				<ol className="space-y-1.5 text-sm">
					{occ.statusHistory.map((h, i) => (
						<li key={i} className="text-xs">
							{h.at.replace('T', ' ')} · {h.by}: {h.from ?? '—'} → <b>{h.to}</b>
							{h.justification && ` · “${h.justification}”`}
						</li>
					))}
					{occ.statusHistory.length === 0 && (
						<p className="text-xs text-muted-foreground">Sem transições.</p>
					)}
				</ol>
			</Panel>
		</div>
	)
}
